<i18n>
  {
    "fr": {
      "admin": {
        "import-data": {
          "title": "Importation de données",
          "data-canvas-to-use-is": "Sélectionner un fichier au format CSV contenant les informations suivantes:",
          "data-canvas": "observationPointId,datasourceId,event,value,quality,remark,measureType,temporalReference",
          "data-canvas-info": "L'enête doit être présent dans la première ligne. Le nom des champs ainsi que leur ordre dans l'entête doit être respecté. Les champs observationPointId et datasourceId sont exclusifs, seul un seul de ces champs doit être renseigné. Si la source de données n'existent pas encore, vous pouvez précisier uniquement le champ observationPointId, le processus d'importation vous aidera à créer le nécessaire.",
          "line": "Ligne",
          "choose-file": "Choisir un fichier",
          "parse-errors": "Erreurs lors de la lecture du fichier:",
          "parse-errors-info": "Les lignes suivantes ont été ignorées:",
          "summary": "Résumé de l'import",
          "running-title": "Importation en cours",
          "datasource": "Source de donnée",
          "unknown-datasource": "Aucune source de données",
          "number-to-import": "Nb de données",
          "error": {
            "bad-header": "L'entête utilisé dans le fichier est incorrect."
          },
          "import-option": {
            "dryRun": "Essai d'importation (dry-run)",
            "force": "Ignorer les erreurs lors de l'import",
            "updateIfExists": "M.à.j si la valeure existe déjà"
          },
          "confirm-import-title": "Confirmer l'importation des données",
          "confirm-import-desc": "Cette action va écrire les données dans les chroniques avec les options d'importations choisi. L'option \"Essai d'importation (dry-run)\" permet d'effectuer un import en condition réel sans impacter les données.",
          "observation-point": "Point d'observation",
          "import-status": "Status d'import",
          "status": {
            "undefined": "En attente",
            "idle": "En attente",
            "running": "En cours",
            "error": "Erreur",
            "warning": "Attention requise",
            "done": "Importé",
            "dry-run-done": "Importé (dry-run)",
            "create-datasource-first": "Création de la source de données nécessaire."
          },
          "show-error": "Voir...",
          "error-detail-title": "Détail de l'erreur",
          "refresh-materialized-views": "Mise à jour des vues matérialisées (statistiques des sources de données)",
          "clean-remote-datasources": "Nettoyage des sources de données télétransmises",
          "done": "Import terminé",
          "create-datasource-first": "Des sources de données doivent être crée avant de pouvoir continuer l'importation",
          "create-datasource-first-desc": "Toutes les données ne disposant pas actuellement d'une source de donnée seront assignées à une nouvelle source de donnée respectant les critères suivants. Si la source de donnée remplissant ces critères existe déjà, elle sera utilisée. Veuillez noter que ces informations affectera l'entier de l'importation ! Il est indispensable que toutes les données soient du même paramètre mesurés et du même type de source de donnée.",
          "selected-parameter-type": "Paramètre mesuré",
          "selected-datasource-type": "Type de source de données",
          "create-datasources": "Créer"
        }
      },
      "common": {
        "values": "Aucune valeur | valeur | valeurs",
        "cancel": "Annuler",
        "execute": "Exécuter",
        "close": "Fermer",
        "restart": "Recommencer",
        "show-all": "Voir tout"
      }
    }
  }
</i18n>

<template>
  <div class="admin import-data" v-if="!loading">
    <h1>{{ $t('admin.import-data.title') }}</h1>
    <div class="import-step file" v-if="!isImportRunning && importStep !== 'done'">
      <h2>{{ $t('admin.import-data.choose-file') }}</h2>
      <p>{{ $t('admin.import-data.data-canvas-to-use-is') }} <pre>{{ $t('admin.import-data.data-canvas') }}</pre></p>
      <p>{{ $t('admin.import-data.data-canvas-info') }}</p>
      <div class="file-input">
        <md-progress-spinner v-if="fileLoading" class="loading-animation" :md-diameter="20" :md-stroke="4" md-mode="indeterminate"></md-progress-spinner>
        <input type="file" @change="onFileChange" :disabled="fileLoading" />
      </div>
      <p class="error" v-if="loadingFileError !== null">{{ this.$t(`admin.import-data.error.${loadingFileError}`) }}</p>
    </div>
    <div class="parse-errors" v-if="parseErrors.length > 0">
      <h2>{{ $t('admin.import-data.parse-errors') }}</h2>
      <p>{{ $t('admin.import-data.parse-errors-info') }}</p>
      <pre v-for="error, i in parseErrors" :key="i">{{ $t('admin.import-data.line') }} {{ error.line }}: {{ error.message }}</pre>
    </div>
    <div class="import-step summary" v-if="showSummary">
      <div class="import-step-title">
        <h2>{{ $t(isImportRunning ? 'admin.import-data.running-title' : 'admin.import-data.summary') }} <span v-if="isImportRunning">({{ this.importProgress }} / {{ this.datasourcesToImport.length }})</span></h2>
        <div class="status-filter">
          <div :class="['import-status', {'selected': selectedStatusFilter === 'showAll'}]" @click="selectedStatusFilter = 'showAll'">
            <span class="import-status-name">{{ $t('common.show-all') }} ({{ datasourcesToImport.length }})</span>
          </div>
          <div :class="['import-status', {'selected': selectedStatusFilter === 'idle'}]" @click="selectedStatusFilter = 'idle'">
            <div class="import-status-icon idle"></div>
            <span class="import-status-name">{{ $t('admin.import-data.status.idle') }} ({{ countIdle }})</span>
          </div>
          <div :class="['import-status', {'selected': selectedStatusFilter === 'running'}]" @click="selectedStatusFilter = 'running'">
            <div class="import-status-icon running"></div>
            <span class="import-status-name">{{ $t('admin.import-data.status.running') }} ({{ countRunning }})</span>
          </div>
          <div :class="['import-status', {'selected': selectedStatusFilter === 'warning'}]" @click="selectedStatusFilter = 'warning'">
            <div class="import-status-icon warning"></div>
            <span class="import-status-name">{{ $t('admin.import-data.status.warning') }} ({{ countWarning }})</span>
          </div>
          <div :class="['import-status', {'selected': selectedStatusFilter === 'error'}]" @click="selectedStatusFilter = 'error'">
            <div class="import-status-icon error"></div>
            <span class="import-status-name">{{ $t('admin.import-data.status.error') }} ({{ countError }})</span>
          </div>
          <div :class="['import-status', {'selected': selectedStatusFilter === 'done'}]" @click="selectedStatusFilter = 'done'">
            <div class="import-status-icon done"></div>
            <span class="import-status-name">{{ $t('admin.import-data.status.done') }} ({{ countDone }})</span>
          </div>
        </div>
      </div>
      <div class="import-job-list">
        <div class="import-job md-elevation-2" v-for="datasource in filtredDatasourcesToImport" :key="datasource.id">
          <h3 v-if="datasource.id !== null">{{ datasource.parameterType.name[$i18n.locale] }} - {{ datasource.datasourceType.name[$i18n.locale] }} (#{{ datasource.id }})</h3>
          <h3 v-if="datasource.id === null">{{ $t('admin.import-data.unknown-datasource') }}</h3>
          <p :class="datasource.importStatus"><b>{{ $t('admin.import-data.import-status') }}</b>: {{ $t(`admin.import-data.status.${datasource.importStatus}`) }} <span class="show-error" @click="showError(datasource)" v-if="datasource.importStatus === 'error'">{{ $t('admin.import-data.show-error') }}</span></p>
          <p><b>{{ $t('admin.import-data.observation-point') }}</b>: {{ datasource.observationPoint.name }} (<router-link :to="{name: 'observation-point-data', params: { id: datasource.observationPoint.id }}" target="_blank">#{{ datasource.observationPoint.id }}</router-link>)</p>
          <p><b>{{ $t('admin.import-data.number-to-import') }}</b>: {{ datasource.data.length }} {{ $tc('common.values', datasource.data.length) }}</p>
        </div>
      </div>
      <div class="datasource-creation" v-if="needCreateDatasources">
        <h3>{{ $t('admin.import-data.create-datasource-first') }}</h3>
        <p>{{ $t('admin.import-data.create-datasource-first-desc') }}</p>
        <div class="create-datasource-selection">
          <md-field>
            <label>{{ $t('admin.import-data.selected-parameter-type') }}</label>
            <md-select v-model="selectedParameterTypeId" :disabled="creatingDatasources">
              <md-option v-for="parameterType in availableParameterTypes" :key="parameterType.code" :value="parameterType.id">{{ parameterType.code }} - {{ parameterType.name[$i18n.locale] }}</md-option>
            </md-select>
          </md-field>
          <md-field>
            <label>{{ $t('admin.import-data.selected-datasource-type') }}</label>
            <md-select v-model="selectedDatasourceTypeId" :disabled="creatingDatasources">
              <md-option v-for="datasourceType in availableDatasourceTypes" :key="datasourceType.code" :value="datasourceType.id">{{ datasourceType.name[$i18n.locale] }}</md-option>
            </md-select>
          </md-field>
          <md-button class="md-primary md-raised" @click="createDatasources" :disabled="selectedDatasourceTypeId === null || selectedParameterTypeId === null || creatingDatasources">{{ $t('admin.import-data.create-datasources') }}</md-button>
        </div>
      </div>
      <div class="import-option">
        <md-switch class="md-primary" v-model="importOption.dryRun" :disabled="isImportRunning || needCreateDatasources">{{ $t('admin.import-data.import-option.dryRun') }}</md-switch>
        <md-switch class="md-primary" v-model="importOption.force" :disabled="isImportRunning || needCreateDatasources">{{ $t('admin.import-data.import-option.force') }}</md-switch>
        <md-switch  class="md-primary" v-model="importOption.updateIfExists" :disabled="isImportRunning || needCreateDatasources">{{ $t('admin.import-data.import-option.updateIfExists') }}</md-switch>
        <md-button @click="onRunClick" class="md-primary md-raised" :disabled="isImportRunning || needCreateDatasources || importIsDone">{{ $t('common.execute') }}</md-button>
      </div>
    </div>
    <md-dialog-confirm
      :md-active.sync="showConfirmImport"
      :md-title="$t('admin.import-data.confirm-import-title')"
      :md-content="$t('admin.import-data.confirm-import-desc')"
      :md-confirm-text="$t('common.execute')"
      :md-cancel-text="$t('common.cancel')"
      @md-cancel="showConfirmImport = false"
      @md-confirm="run" />

    <md-dialog class="import-error-detail" :md-active.sync="showErrorDetail">
      <md-dialog-title>{{ $t('admin.import-data.error-detail-title') }}</md-dialog-title>

      <md-dialog-content v-if="datasourceErrorDetail !== null">
        <div v-for="error, index in datasourceErrorDetail.importError" :key="`e_${index}`">
          <pre>{{ error.reason.routine }}</pre>
          <pre>{{ error.reason.detail }}</pre>
          <pre>{{ error.reason.hint }}</pre>
          <pre>{{ error.reason.where }}</pre>
        </div>
      </md-dialog-content>

      <md-dialog-actions>
        <md-button class="md-primary" @click="closeError">{{ $t('common.close') }}</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment-timezone'

import TimeseriesProxy from '@/proxies/timeseries'
import ObservationPointProxy from '@/proxies/observationPoint'

const indexes = []
indexes['observationPointId'] = 0
indexes['datasourceId'] = 1
indexes['event'] = 2
indexes['value'] = 3
indexes['quality'] = 4
indexes['remark'] = 5
indexes['measureType'] = 6
indexes['temporalReference'] = 7

export default {
  name: 'ObservationPointDatasourceAdmin',
  computed: {
    remainingDatasourcesToImport () {
      return this.datasourcesToImport.filter((datasource) => datasource.importStatus !== 'running').length
    },
    importProgress () {
      return this.datasourcesToImport.length  - this.remainingDatasourcesToImport
    },
    showSummary () {
      return this.parseErrors.length === 0 && this.fileLoaded
    },
    needCreateDatasources () {
      return this.datasourcesToImport.filter((datasource) => datasource.importStatus === 'create-datasource-first').length > 0
    },
    countIdle () {
      return this.datasourcesToImport.filter((datasource) => datasource.importStatus === 'idle' || typeof datasource.importStatus === 'undefined').length
    },
    countRunning () {
      return this.datasourcesToImport.filter((datasource) => datasource.importStatus === 'running').length
    },
    countWarning () {
      return this.datasourcesToImport.filter((datasource) => datasource.importStatus === 'create-datasource-first').length
    },
    countError () {
      return this.datasourcesToImport.filter((datasource) => datasource.importStatus === 'error').length
    },
    countDone () {
      return this.datasourcesToImport.filter((datasource) => datasource.importStatus === 'done' || datasource.importStatus === 'dry-run-done').length
    },
    filtredDatasourcesToImport () {
      switch (this.selectedStatusFilter) {
        case 'idle': return this.datasourcesToImport.filter((datasource) => datasource.importStatus === 'idle' || typeof datasource.importStatus === 'undefined')
        case 'running': return this.datasourcesToImport.filter((datasource) => datasource.importStatus === 'running')
        case 'warning': return this.datasourcesToImport.filter((datasource) => datasource.importStatus === 'create-datasource-first')
        case 'error': return this.datasourcesToImport.filter((datasource) => datasource.importStatus === 'error')
        case 'done': return this.datasourcesToImport.filter((datasource) => datasource.importStatus === 'done' || datasource.importStatus === 'dry-run-done')
        case 'showAll':
        default:
          return this.datasourcesToImport
      }
    },
    importIsDone () {
      return this.datasourcesToImport.filter((datasource) => datasource.importStatus === 'done').length === this.datasourcesToImport.length
    }
  },
  data () {
    return {
      loading: false,
      fileLoading: false,
      fileLoaded: false,
      creatingDatasources: false,
      datasourcesToImport: [],
      loadingFileError: null,
      parseErrors: [],
      importOption: {
        dryRun: true,
        force: false,
        updateIfExists: false
      },
      showConfirmImport: false,
      rejectedJobs: [],
      isImportRunning: false,
      importStep: 'idle',
      showErrorDetail: false,
      datasourceErrorDetail: null,
      selectedParameterTypeId: null,
      selectedDatasourceTypeId: null,
      availableParameterTypes: [],
      availableDatasourceTypes: [],
      selectedStatusFilter: 'showAll'
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    load () {
      this.loading = true
      TimeseriesProxy.listParameterTypes().then((parameterTypes) => {
        TimeseriesProxy.listDatasourceTypes().then((datasourceTypes) => {
          this.availableParameterTypes = parameterTypes
          this.availableDatasourceTypes = datasourceTypes
          this.loading = false
        })
      })
    },
    checkRow(row) {
      if (
        (typeof row[indexes['observationPointId']] === 'undefined' || row[indexes['observationPointId']] == '') &&
        (typeof row[indexes['datasourceId']] === 'undefined' || row[indexes['datasourceId']] == '')
      ) throw new Error('Au moins un des champs suivants doit être rempli: datasourceId, observationPointId')
      if (typeof row[indexes['event']] === 'undefined' || row[indexes['event']] == '') throw new Error('La date n\'est pas définie.')
      if (typeof row[indexes['temporalReference']] === 'undefined' || row[indexes['temporalReference']] == '') throw new Error('La référence temporelle n\'est pas définie.')
      if (row[indexes['remark']] !== 'undefined' && row[indexes['remark']].length > 5) throw new Error('Le champ remark ne peut pas dépasser 5 charactères.')
    },
    parseDate (date, tempRef) {
      // Contrôle si le datetime contient des milisecondes.
      if (date.split(".").length > 1) {
        // Si c'est le cas, on corrige la date final (problème avec les fichiers excels sources)
        let tmp = date.split(".")
        date = moment.utc(tmp[0], "YYYY-MM-DDTHH:mm:SS")
        if (parseInt(tmp[1]) >= 500) date.add(1, 'seconds')
      }
      else {
        date = moment.utc(date, "YYYY-MM-DDTHH:mm:SS")
      }
      // Assignation de la timezone suivant le paramètre tempRef
      switch (this.convertTemporalReference(tempRef)) {
        case 'UTC':     return date.utcOffset(0, true)
        case 'UTC+1':   return date.utcOffset(60, true)
        case 'UTC+2':   return date.utcOffset(120, true)
        default:
        case 'legal':
        case 'unknown': return date.tz('Europe/Zurich', true)
      }
    },
    convertQualityCode(qualityCd) {
      switch(qualityCd) {
        case '0': return 'not_validated'
        case '1': return 'validated'
        case '2': return 'to_check'
        case '3': return 'corrected'
        case '4': return 'reconstituted'
        case '5': return 'unreliable'
        default: return 'unknown'
      }
    },
    convertMeasureType(measurementTypeCd) {
      switch(measurementTypeCd) {
        case 'M': return 'manual'
        case 'D': return 'auto_digital'
        case 'G': return 'auto_digitized'
        case 'T': return 'auto_teletransmitted'
        default: return 'unknown'
      }
    },
    convertTemporalReference(tempRef) {
      switch(tempRef) {
        case '0': return 'UTC'
        case '1': return 'UTC+1'
        case '2': return 'UTC+2'
        case '3': return 'legal'
        default: return 'unknown'
      }
    },
    parseId (id) {
      let toReturn = parseInt(id)
      if (isNaN(toReturn)) return null
      return toReturn
    },
    parseRow(row) {
      return {
        'observationPointId': this.parseId(row[indexes['observationPointId']]),
        'datasourceId': this.parseId(row[indexes['datasourceId']]),
        'event': this.parseDate(row[indexes['event']], row[indexes['temporalReference']]),
        'value': parseFloat(row[indexes['value']]),
        'quality': this.convertQualityCode(row[indexes['quality']]),
        'remark': typeof row[indexes['remark']] === 'undefined' ? '' : row[indexes['remark']],
        'measureType': this.convertMeasureType(row[indexes['measureType']]),
        'temporalReference': this.convertTemporalReference(row[indexes['temporalReference']])
      }
    },
    onFileChange: function(event) {
      this.fileLoaded = false
      this.loadingFileError = null
      this.parseErrors = []
      this.fileLoading = true
      this.datasourcesToImport = []

      const file = event.target.files[0]
      const reader = new FileReader()
      reader.onload = (evt) => {
        let rows = evt.target.result.split(/\r?\n/)
        // Contrôle de l'entête
        if (rows[0] !== this.$t('admin.import-data.data-canvas')) {
          this.loadingFileError = 'bad-header'
          this.fileLoading = false
          return
        }

        let row
        let dataToImport = []
        rows.forEach((d, i) => {
          if (i === 0) return
          row = d.split(',')
          if (row.length === 0 || row === '' || row.length === 1 && row[0] === '') return
          try { this.checkRow(row) } catch(e) { this.parseErrors.push({ message: e, line: i + 1 }) }
          try { dataToImport.push(this.parseRow(row)) } catch(e) { this.parseErrors.push({ message: e, line: i + 1 }) }
        })

        // Regroupement des données par source de données existante
        let dataWithExistingDatasourceToImport = _.groupBy(dataToImport.filter((d) => d.datasourceId !== null), 'datasourceId')
        let dataWithoutDatasourceToImport = _.groupBy(dataToImport.filter((d) => d.datasourceId === null), 'observationPointId')

        // Récupération des informations sur les sources de données existantes
        let toFetch = []
        for(const [key] of Object.entries(dataWithExistingDatasourceToImport)) {
          toFetch.push(TimeseriesProxy.getDatasource(key))
        }

        Promise.all(toFetch).then((datasources) => {
          toFetch = []
          datasources.forEach((datasource) => {
            // Récupération du point d'observation des sources de données existantes
            toFetch.push(ObservationPointProxy.get(datasource.observationPointId))
          })

          for(const [key] of Object.entries(dataWithoutDatasourceToImport)) {
            // Récupération du point d'observation des données n'ayant pas de sources de données existantes
            toFetch.push(ObservationPointProxy.get(key))
          }

          Promise.all(toFetch).then((observationPoints) => {
            for(const [key] of Object.entries(dataWithoutDatasourceToImport)) {
              this.datasourcesToImport.push({
                id: null,
                data: dataWithoutDatasourceToImport[key],
                observationPoint: observationPoints.find((obsPoint) => obsPoint.id === parseInt(key)),
                importStatus: 'create-datasource-first'
              })
            }

            datasources.forEach((datasource) => {
              this.datasourcesToImport.push({
                ...datasource,
                data: dataWithExistingDatasourceToImport[datasource.id],
                observationPoint: observationPoints.find((obsPoint) => obsPoint.id === datasource.observationPointId),
                importStatus: 'idle'
              })
            })

            this.fileLoading = false
            this.fileLoaded = true
          }).catch((e) => { throw e })
        }).catch((e) => { throw e })
      }
      reader.readAsText(file)
    },
    onRunClick () {
      if (!this.importOption.dryRun) return this.showConfirmImport = true
      this.run()
    },
    executeImportJob (datasource) {
      return new Promise((resolve, reject) => {
        datasource.importStatus = 'running'
        this.$set(this.datasourcesToImport, this.datasourcesToImport.indexOf(datasource), datasource)

        TimeseriesProxy.importValues({
          datasourceId: datasource.id,
          data: datasource.data,
          ...this.importOption
        }).then((result) => {
          // Cas dry-run: le status de l'import est retourné dans le résultat de la requête
          if (this.importOption.dryRun) {
            if (result.filter((job) => job.status !== 'fulfilled').length > 0) {
              datasource.importStatus = 'error'
            }
            else datasource.importStatus = 'dry-run-done'
            datasource.importError = result.filter((job) => job.status !== 'fulfilled')
          } else datasource.importStatus = 'done'

          this.$set(this.datasourcesToImport, this.datasourcesToImport.indexOf(datasource), datasource)
          resolve()
        }).catch((e) => {
          datasource.importStatus = 'error'
          datasource.importError = e.response.data
          this.$set(this.datasourcesToImport, this.datasourcesToImport.indexOf(datasource), datasource)
          reject(e)
        })
      })
    },
    run () {
      this.datasourcesToImport.forEach((datasource) => datasource.importStatus = 'idle')
      this.importStep = 'importing'
      this.isImportRunning = true
      // Exécution séquentielle des tâches
      let toRun = []

      this.datasourcesToImport.forEach((datasource) => {
        toRun.push(this.executeImportJob.bind(this, datasource))
      })

      toRun.reduce((previousJob, currentJob) => { return previousJob.then(() => currentJob.call()).catch((error) => this.rejectedJobs.push({
        error,
        job: previousJob
      })) }, Promise.resolve()).finally(() => {
        this.isImportRunning = false
        this.importStep = 'idle'
      })
    },
    showError (datasource) {
      this.showErrorDetail = true
      this.datasourceErrorDetail = datasource
    },
    closeError () {
      this.showErrorDetail = false
      this.datasourceErrorDetail = null
    },
    createDatasources () {
      this.creatingDatasources = true
      // Essai de récupérer une source de donnée existante
      let found
      this.datasourcesToImport.forEach((datasource, i) => {
        if (datasource.id !== null) return

        found = datasource.observationPoint.datasources.find((ds) => ds.parameterType.id === this.selectedParameterTypeId && ds.datasourceType.id === this.selectedDatasourceTypeId)
        if (typeof found !== 'undefined') {
          this.$set(this.datasourcesToImport, i, {
            ...datasource,
            ...found,
            importStatus: 'idle'
          })
        }
      })

      // Création des sources de données manquantes
      let emptyDatasources = this.datasourcesToImport.filter((datasource) => datasource.id === null)
      let toCreate = []
      emptyDatasources.forEach((datasource) => {
        toCreate.push(TimeseriesProxy.createDatasource(datasource.observationPoint.id, this.selectedParameterTypeId, this.selectedDatasourceTypeId))
      })

      Promise.all(toCreate).then((createdDatasources) => {
        let found
        createdDatasources.forEach((createdDatasource) => {
          found = emptyDatasources.find((ds) => ds.observationPoint.id === createdDatasource.observationPointId)
          if (typeof found !== 'undefined') {
            this.$set(this.datasourcesToImport, this.datasourcesToImport.indexOf(found), {
              ...found,
              ...createdDatasource,
              importStatus: 'idle'
            })
          }
        })
        this.creatingDatasources = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  .file-input {
    display: flex;
    justify-content: center;
  }

  .import-job-list {
    margin: 20px;
    padding: 20px;
    background: #edebeb;
    max-height: 400px;
    overflow: auto;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 15px;

    .import-job {
      background: white;
      padding: 15px;
      width: 340px;
      user-select: none;

      h3 {
        margin-top: 0px;
        margin-bottom: 7px;
      }

      p {
        margin: 0px;
        margin-left: 15px;

        &.undefined, &.idle {
          color: grey;
        }

        &.done, &.dry-run-done {
          color: green;
        }

        &.error {
          color: #ff1744;
        }

        &.create-datasource-first {
          color: rgb(236, 201, 3);
        }
      }

      .show-error {
        color: #23a3dd;
        margin-left: 10px;
        cursor: pointer;
        text-decoration: underline;
      }
    }
  }

  .import-option {
    margin: 20px;
    padding: 20px;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
  }

  .datasource-creation {
    margin: 20px;
    padding: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    flex-direction: column;
  }

  .import-data {
    max-width: 70%;
    margin: auto;
  }

  .import-error-detail .md-content {
    padding: 26px;
    overflow: auto;
  }

  .refresh-materialized-views, .clean-remote-datasources {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 20px;

    .md-progress-spinner  {
      width: 20px;
      height: 20px;
    }
  }

  .create-datasource-selection {
    display: flex;
    gap: 15px;
    .md-field {
      width: 350px;
    }
  }

  .status-filter {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-left: 20px;

    .import-status {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 5px;
      cursor: pointer;
      user-select: none;

      &.selected {
        text-decoration: underline;
      }

      .import-status-icon {
        width: 5px;
        height: 5px;
        border-radius: 5px;

        &.idle {
          background-color: grey;
        }

        &.running {
          background-color: rgb(40, 69, 161);
        }

        &.done {
          background-color: green;
        }

        &.error {
          background-color: #ff1744;
        }

        &.warning {
          background-color: rgb(236, 201, 3);
        }
      }
    }
  }
</style>